/**
 * 计时账目。刻意做成纯函数：整个「切走了要不要算时间」的判断都在这里，
 * 不碰 DOM、不碰 React，可以单独跑测试。
 *
 * ── 为什么不靠 visibilitychange ──
 * 原来的做法是在 visibilitychange 变 hidden 的那一刻把计时停掉。iOS 上这不可靠：
 * 切到别的 App 时 PWA 会被系统挂起，处理器不保证能在挂起前跑完。一旦没跑到，
 * 段起点还留着，回来时 now - segStart 把整段离开时间全算进去 ——
 * 这就是「切走了还在计时」的来源。
 *
 * 现在改用一个不会骗人的信号：**页面被挂起时，定时器一定停**。
 * 每秒的心跳只要迟到超过 FREEZE_MS，就说明这中间页面没在跑，那段时间一律不算。
 * 不需要任何事件配合；事件只是让界面反应更快一点。
 *
 * 离屏模式不做扣除 —— 那时候本来就是要「人不看屏幕也继续计」。
 */
import type { StudyMode } from './study'

export interface Clock {
  /** 已确认累计（毫秒） */
  accumMs: number
  /** 当前这一段的起点；null 表示暂停中 */
  segStart: number | null
  /** 最后一次确认「页面还活着」的时刻 */
  lastTick: number
}

/** 心跳 1 秒一次。迟到 4 秒以上只可能是被冻结，不会是普通抖动。 */
export const FREEZE_MS = 4000

/**
 * 低于这个时长的中断不打扰人。
 * 冻结检测天生会捕捉到很多几秒钟的小空档（主线程忙、系统调度），那些照样不计入 ——
 * 但为几秒钟弹一个「刚才中断了 0 分钟，算上还是不算」纯属噪音，
 * 弹两次之后这个提示就再也不会被认真看了。
 */
export const ASK_MS = 60_000

export const newClock = (now: number, running = true): Clock =>
  ({ accumMs: 0, segStart: running ? now : null, lastTick: now })

export const liveMs = (c: Clock, now: number): number =>
  c.accumMs + (c.segStart !== null ? Math.max(0, now - c.segStart) : 0)

export const liveSecs = (c: Clock, now: number): number => Math.floor(liveMs(c, now) / 1000)

/** 结算到 upTo 并暂停 */
export function commit(c: Clock, upTo: number): Clock {
  if (c.segStart === null) return c
  return { accumMs: c.accumMs + Math.max(0, upTo - c.segStart), segStart: null, lastTick: c.lastTick }
}

/**
 * 每秒心跳。返回新状态，以及「刚才被冻结了多久」（0 表示正常）。
 * 这是正确性的唯一来源 —— 即使 visibilitychange 一次都不触发，时长也是对的。
 */
export function onTick(c: Clock, now: number, mode: StudyMode): { clock: Clock; frozenMs: number } {
  const drift = now - c.lastTick
  if (mode !== 'onscreen' || drift <= FREEZE_MS || c.segStart === null) {
    return { clock: { ...c, lastTick: now }, frozenMs: 0 }
  }
  // 这中间页面没在跑：结算到「最后一次确认还活着」，中间丢掉，从现在重新起一段
  const settled = commit(c, c.lastTick)
  return { clock: { ...settled, segStart: now, lastTick: now }, frozenMs: drift }
}

/** 页面转入后台。能跑到就更准，跑不到由 onTick 兜底。 */
export function onHidden(c: Clock, now: number, mode: StudyMode): Clock {
  if (mode !== 'onscreen') return { ...c, lastTick: now }
  return { ...commit(c, now), lastTick: now }
}

/**
 * 回到前台。
 *
 * ⚠️ 这里必须自己把离开的那段结算掉，不能指望 onHidden 已经跑过。
 * iOS 上 hidden 常常来不及触发，而 visible 一定会触发；如果这里只是把 lastTick
 * 刷成当前时刻，就等于**亲手抹掉了冻结检测赖以工作的证据** ——
 * 下一次 tick 看到的 drift 是 0，而 segStart 还停在离开之前，
 * 于是整段离开时间被原样计入。这正是「切走了还在计时」的另一条路径。
 */
export function onVisible(c: Clock, now: number, mode: StudyMode): { clock: Clock; awayMs: number } {
  const away = now - c.lastTick
  if (mode !== 'onscreen') return { clock: { ...c, lastTick: now }, awayMs: 0 }

  // segStart 还在 = onHidden 没跑过。自己结算到「最后一次确认还活着」那一刻。
  const settled = away > FREEZE_MS && c.segStart !== null ? commit(c, c.lastTick) : c
  return {
    // 只有真的结算过（或本来就暂停着）才重开一段。
    // 无条件写 segStart = now 会把当前这一段还没结算的时间整段丢掉 ——
    // 短暂切换一下回来，30 秒就没了。
    clock: { ...settled, segStart: settled.segStart ?? now, lastTick: now },
    awayMs: away > FREEZE_MS ? away : 0,
  }
}

/** 一键补回：把刚才判掉的那段加回累计。 */
export const addBack = (c: Clock, ms: number): Clock => ({ ...c, accumMs: c.accumMs + Math.max(0, ms) })

/**
 * 从服务端的会话状态重建时钟。
 *
 * 两个坑，2026-09-01 代码审查时发现，都是用户直接看得见的：
 *
 * ① 服务端的 paused 是「最后一次心跳时的状态」。如果他切走（暂停）之后回来、
 *    页面又重新加载了，那一刻他正盯着屏幕，但 visibilitychange 不会触发
 *    （加载时就已经是 visible），于是时钟永远停在暂停态，怎么等都不走。
 *    → 只要当前页面可见，就恢复成运行态。
 *
 * ② 离屏会话的 activeSeconds 严重偏小：那段时间心跳是断的
 *    （实测服务端记 19 秒、实际已过 101 分钟）。直接拿它当累计，
 *    重开 App 会看到「00:19」。→ 离屏按开始时间算墙上时间，取两者较大。
 */
export function restore(
  open: { activeSeconds: number; paused: boolean; startedAt?: string | null },
  mode: StudyMode,
  now: number,
  visible = true,
): Clock {
  let accumMs = open.activeSeconds * 1000;
  if (mode === 'offscreen' && open.startedAt) {
    const started = new Date(String(open.startedAt).replace(' ', 'T')).getTime()
    if (Number.isFinite(started) && started <= now) accumMs = Math.max(accumMs, now - started)
  }
  const running = mode === 'offscreen' || visible || !open.paused
  return { accumMs, segStart: running ? now : null, lastTick: now }
}